// src/authpages/timetables/StudyTimetable.js
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, BookOpen } from 'lucide-react';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import CalendarExport from './CalendarExport';

// Import calendar connection components
import GoogleCalendarConnect from './GoogleCalendarConnect';
import MicrosoftCalendarConnect from './MicrosoftCalendarConnect';
import AppleCalendarConnect from './AppleCalendarConnect';
import CelcatCalendarConnect from './CelcatCalendarConnect';

// Import calendar view and study timetable generator
import CalendarView from './CalendarView';
import StudyTimetableGenerator from './StudyTimetableGenerator';

const Timetable = () => {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [activeCalendars, setActiveCalendars] = useState({
    google: false,
    microsoft: false,
    apple: false,
    celcat: false
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsUserLoggedIn(true);
        setUserEmail(user.email || '');
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleCalendarData = (events, source) => {
    setCalendarEvents(prevEvents => {
      // Filter out existing events from the same source
      const filteredPrevEvents = prevEvents.filter(event => event.provider !== source);
      
      // Add new events
      const newEvents = events.map(event => ({
        ...event,
        provider: source
      }));
      
      return [...filteredPrevEvents, ...newEvents];
    });

    // Update active calendars
    setActiveCalendars(prev => ({
      ...prev,
      [source.toLowerCase().split(' ')[0]]: events.length > 0
    }));
  };

  const handleStudySchedule = (studySlots) => {
    setCalendarEvents(prev => {
      // Filter out any existing study slots
      const filteredEvents = prev.filter(event => event.provider !== 'Study Schedule');
      return [...filteredEvents, ...studySlots];
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-900">My Timetable</h1>
          <p className="text-gray-600">Manage your schedule and sync your calendars</p>
        </div>

        {/* Calendar Integration Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h5 className="mb-0 font-medium flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                Google Calendar
              </h5>
            </div>
            <div className="p-6">
              <GoogleCalendarConnect 
                onCalendarEvents={(events) => handleCalendarData(events, 'Google Calendar')} 
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h5 className="mb-0 font-medium flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                Microsoft Calendar
              </h5>
            </div>
            <div className="p-6">
              <MicrosoftCalendarConnect 
                onCalendarEvents={(events) => handleCalendarData(events, 'Microsoft Calendar')} 
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h5 className="mb-0 font-medium flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                Apple Calendar
              </h5>
            </div>
            <div className="p-6">
              <AppleCalendarConnect 
                onCalendarEvents={(events) => handleCalendarData(events, 'Apple Calendar')} 
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h5 className="mb-0 font-medium flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                Celcat Timetable
              </h5>
            </div>
            <div className="p-6">
              <CelcatCalendarConnect 
                onCalendarEvents={(events) => handleCalendarData(events, 'Celcat Calendar')} 
              />
            </div>
          </div>
        </div>

        {/* Active Calendars Summary */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h5 className="font-medium mb-3">Active Data Sources</h5>
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm ${activeCalendars.google ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
              Google Calendar {activeCalendars.google ? '✓' : ''}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm ${activeCalendars.microsoft ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
              Microsoft Calendar {activeCalendars.microsoft ? '✓' : ''}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm ${activeCalendars.apple ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
              Apple Calendar {activeCalendars.apple ? '✓' : ''}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm ${activeCalendars.celcat ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
              Celcat Timetable {activeCalendars.celcat ? '✓' : ''}
            </span>
          </div>
        </div>

        {/* Calendar View */}
<div className="mb-8 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <CalendarView events={calendarEvents} />
</div>

{/* NEW: Calendar Export */}
<div className="mb-6">
  <CalendarExport 
    events={calendarEvents} 
    calendarName={`StudyAce - ${userEmail}`} 
  />
</div>

{/* Study Timetable Generator */}
<div className="mb-8 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-200">
    <h5 className="mb-0 font-medium flex items-center">
      <BookOpen className="h-5 w-5 text-blue-600 mr-2" />
      Study Timetable Generator
    </h5>
  </div>
  <div className="p-6">
    <StudyTimetableGenerator 
      events={calendarEvents}
      onGenerateSchedule={handleStudySchedule}
    />
  </div>
</div>
</div>
</div>
  );
};

export default Timetable;